import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { User, AuthState, ApiResponse } from '@/types'

async function fetchCurrentUser(): Promise<User | null> {
  const res = await fetch('/api/auth/me')
  if (!res.ok) {
    if (res.status === 401) return null
    throw new Error('Failed to fetch user')
  }
  const data: ApiResponse<User> = await res.json()
  return data.data ?? null
}

async function logoutUser(): Promise<void> {
  const res = await fetch('/api/auth/logout', { method: 'POST' })
  if (!res.ok) throw new Error('Failed to logout')
}

export function useAuth(): AuthState & {
  logout: () => void
  refetch: () => void
} {
  const queryClient = useQueryClient()

  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: fetchCurrentUser,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const logoutMutation = useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'user'], null)
      window.location.href = '/login'
    },
  })

  return {
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading,
    logout: () => logoutMutation.mutate(),
    refetch,
  }
}
